import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaGraduationCap, FaQuestionCircle, FaChartLine, FaSearch, FaArrowUp, FaArrowDown, FaLock, FaChartArea, FaStar, FaComments, FaTrophy, FaUsers, FaFlask } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import PortfolioService from '../services/PortfolioService';
import StockService from '../services/StockService';
import WatchlistService from '../services/WatchlistService';
import toast from 'react-hot-toast';
import PortfolioTimeGraph from './PortfolioTimeGraph';
import PortfolioSelector from './PortfolioSelector';
import soundEffects from '../utils/soundEffects';
import { createSuccessConfetti } from '../utils/confetti';
import './Toggle.css';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import './Portfolio.css';
import './PortfolioSelector.css';
import './Simulations.css';

const Portfolio = ({ simulationsNav = false, onSwitchTab = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, actions } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [stockToSell, setStockToSell] = useState(null);
  const [sellQuantity, setSellQuantity] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTimeGraph, setShowTimeGraph] = useState(false);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [gainLossView, setGainLossView] = useState('alltime'); // 'alltime' or 'daily'
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showWatchlistDropdown, setShowWatchlistDropdown] = useState(false);
  const [showPortfolioDropdown, setShowPortfolioDropdown] = useState(false);
  const [featuredStocks, setFeaturedStocks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [activePortfolio, setActivePortfolio] = useState(null);
  const [isChallengePortfolio, setIsChallengePortfolio] = useState(false);

  // Helper function to safely parse numbers
  const safeParseFloat = (value, defaultValue = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Helper function to detect asset type
  const detectAssetType = (symbol) => {
    const sym = symbol.toUpperCase();
    
    // Common cryptocurrency symbols
    const knownCryptos = [
      'BTC', 'ETH', 'BNB', 'SOL', 'ADA', 'XRP', 'DOT', 'DOGE', 'AVAX', 'SHIB',
      'MATIC', 'LTC', 'UNI', 'LINK', 'ATOM', 'ETC', 'XLM', 'BCH', 'ALGO', 'VET',
      'FIL', 'TRX', 'EOS', 'AAVE', 'THETA', 'XTZ', 'AXS', 'SAND', 'MANA', 'GALA',
      'NEAR', 'FTM', 'ICP', 'HBAR', 'QNT', 'EGLD', 'FLOW', 'CHZ', 'ENJ', 'BAT'
    ];
    
    if (knownCryptos.includes(sym)) {
      return 'crypto';
    }
    
    // Known ETF symbols - ONLY use explicit list, no patterns to avoid false positives
    const knownETFs = [
      // Major Market ETFs
      'SPY', 'QQQ', 'IWM', 'DIA', 'VOO', 'VTI', 'IVV', 'ITOT',
      // Sector SPDRs (XL*)
      'XLK', 'XLF', 'XLV', 'XLE', 'XLI', 'XLP', 'XLY', 'XLB', 'XLU', 'XLC', 'XLRE',
      // Vanguard ETFs (V*)
      'VEA', 'VWO', 'VYM', 'VUG', 'VTV', 'VBR', 'VNQ', 'VGT', 'VHT', 'VFH', 'VDE', 'VIS', 'VDC', 'VCR', 'VAW', 'VPU', 'VOX',
      // iShares ETFs (I*)
      'IWM', 'IVV', 'ITOT', 'IEFA', 'IEMG', 'IAU', 'IWF', 'IWD', 'IYR', 'IWF', 'IWN', 'IWO', 'IJH', 'IJR',
      // Commodity ETFs
      'GLD', 'SLV', 'USO', 'UNG', 'DBA', 'DBC',
      // Bond ETFs
      'BND', 'AGG', 'TLT', 'LQD', 'HYG', 'SHY', 'IEF', 'TIP',
      // International ETFs
      'EFA', 'EEM', 'FXI', 'EWJ', 'EWZ', 'EWY', 'EWW', 'EWA', 'EWC', 'EWG', 'EWH', 'EWI', 'EWL', 'EWN', 'EWO', 'EWP', 'EWQ', 'EWS', 'EWT', 'EWU', 'EWW',
      // Dividend ETFs
      'SCHD', 'DVY', 'HDV', 'VYM', 'SPHD', 'NOBL',
      // Crypto ETFs
      'GBTC', 'ETHE',
      // Other popular ETFs
      'ARKK', 'ARKQ', 'ARKW', 'ARKG', 'ARKF', 'SPDR', 'SPLG', 'SPMD', 'SPTM'
    ];
    
    // Only check known ETFs - no patterns to avoid false positives
    if (knownETFs.includes(sym)) {
      return 'etf';
    }
    
    // Default to stock for everything else
    return 'stock';
  };

  // Authentication check and portfolio data loading
  useEffect(() => {
    const checkAuth = async () => {
      try {
        
        // Check if user is authenticated
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        // Check if user exists in context
        if (!state.user) {
          
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);

        // Check if portfolioId is provided in URL params (for challenge portfolios)
        const portfolioIdFromUrl = searchParams.get('portfolioId');
        
        if (portfolioIdFromUrl) {
          // Load specific portfolio from URL
          try {
            const portfolioData = await PortfolioService.getPortfolio(portfolioIdFromUrl);
            if (portfolioData && portfolioData.portfolio) {
              const portfolio = portfolioData.portfolio;
              setActivePortfolio(portfolio);
              // Check if this is a challenge portfolio (challengeId field exists)
              setIsChallengePortfolio(!!portfolio.challengeId);
              await loadPortfolioData(portfolio);
            } else {
              toast.error('Portfolio not found');
              navigate('/simulations');
            }
          } catch (error) {
            toast.error('Failed to load portfolio');
            navigate('/simulations');
          }
        } else {
          // Load active portfolio first
          const portfolio = await loadActivePortfolio();
          
          // Load portfolio data from backend (use the portfolio we just loaded)
          await loadPortfolioData(portfolio);
        }
      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user, searchParams]);

  // Load watchlist stocks
  const loadWatchlistStocks = async () => {
    try {
      const data = await WatchlistService.getWatchlistWithPrices();
      
      if (data.watchlist && data.watchlist.length > 0) {
        const formattedStocks = data.watchlist.map(stock => ({
          symbol: stock.symbol || 'N/A',
          name: stock.name || stock.symbol || 'Unknown',
          price: parseFloat(stock.currentPrice || stock.price) || 0,
          change: parseFloat(stock.change) || 0,
          changePercent: parseFloat(stock.changePercent) || 0,
          isWatchlist: true
        }));
        setWatchlistStocks(formattedStocks);
      } else {
        setWatchlistStocks([]);
      }
    } catch (error) {
      console.error('Failed to load watchlist stocks:', error);
      setWatchlistStocks([]);
    }
  };

  // Load featured stocks (top 10 most traded)
  const loadFeaturedStocks = async () => {
    try {
      const data = await StockService.getMostTraded(10);
      
      if (data.stocks && data.stocks.length > 0) {
        const formattedStocks = data.stocks.map(stock => ({
          symbol: stock.symbol || 'N/A',
          name: stock.name || stock.symbol || 'Unknown',
          price: parseFloat(stock.price) || 0,
          change: parseFloat(stock.change) || 0,
          changePercent: parseFloat(stock.changePercent) || 0
        }));
        
        // Update prices in place instead of replacing the entire array
        // This preserves the order and prevents the list from clearing
        setFeaturedStocks(prevStocks => {
          if (prevStocks.length === 0) {
            // First load - set initial stocks
            setAvailableStocks(formattedStocks);
            return formattedStocks;
          }
          
          // Update existing stocks with new prices, preserving order
          const updatedStocks = prevStocks.map(prevStock => {
            const newStock = formattedStocks.find(s => s.symbol === prevStock.symbol);
            if (newStock) {
              // Update price data but keep the same object reference when possible
              return {
                ...prevStock,
                price: newStock.price,
                change: newStock.change,
                changePercent: newStock.changePercent
              };
            }
            return prevStock;
          });
          
          // Add any new stocks that weren't in the previous list
          formattedStocks.forEach(newStock => {
            if (!updatedStocks.find(s => s.symbol === newStock.symbol)) {
              updatedStocks.push(newStock);
            }
          });
          
          setAvailableStocks(updatedStocks);
          return updatedStocks;
        });
      } else {
        // Fallback to regular stocks if no trades yet (only on first load)
        setFeaturedStocks(prevStocks => {
          const needsFallback = prevStocks.length === 0;
          if (needsFallback) {
            // Load fallback stocks asynchronously
            loadAvailableStocks();
          }
          return prevStocks; // Keep existing stocks if we have them
        });
      }
    } catch (error) {
      console.error('Failed to load featured stocks:', error);
      // Only fallback if we don't have any stocks yet
      setFeaturedStocks(prevStocks => {
        const needsFallback = prevStocks.length === 0;
        if (needsFallback) {
          // Load fallback stocks asynchronously
          loadAvailableStocks();
        }
        return prevStocks; // Keep existing stocks if we have them
      });
    }
  };

  // Load available stocks from Alpha Vantage
  const loadAvailableStocks = async () => {
    try {
      setStocksLoading(true);
      
      const data = await StockService.getStocks(null, 20);
      
      if (data.stocks && data.stocks.length > 0) {
        // Ensure proper data formatting
        const formattedStocks = data.stocks.map(stock => ({
          symbol: stock.symbol || 'N/A',
          name: stock.name || stock.symbol || 'Unknown',
          price: parseFloat(stock.price) || 0,
          change: parseFloat(stock.change) || 0,
          changePercent: parseFloat(stock.changePercent) || 0
        }));
        setAvailableStocks(formattedStocks);
      } else {
        // No stocks available
        setAvailableStocks([]);
      }
    } catch (error) {
      // No fallback stocks available
      setAvailableStocks([]);
      toast.error('Failed to load stock data. Please try again.');
    } finally {
      setStocksLoading(false);
    }
  };

  // Load stocks when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      loadWatchlistStocks();
      // Only set loading on initial load
      setStocksLoading(true);
      loadFeaturedStocks().finally(() => {
        setStocksLoading(false);
      });
    }
  }, [isAuthenticated]);

  // Track last load time to prevent rapid successive calls
  const lastLoadTimeRef = useRef(0);
  const isLoadingRef = useRef(false);

  // Reload portfolio data when active portfolio changes (with debouncing)
  useEffect(() => {
    if (activePortfolio && isAuthenticated) {
      const now = Date.now();
      // Debounce: only load if last load was more than 2 seconds ago
      if (now - lastLoadTimeRef.current > 2000 && !isLoadingRef.current) {
        lastLoadTimeRef.current = now;
        loadPortfolioData(activePortfolio);
      }
    }
  }, [activePortfolio?.id, isAuthenticated]); // Only depend on portfolio ID, not the whole object

  // Auto-refresh portfolio data every 30 seconds to prevent exploit
  useEffect(() => {
    if (!isAuthenticated || !activePortfolio) return;

    const refreshInterval = setInterval(() => {
      const now = Date.now();
      // Only refresh if not currently loading and last load was more than 2 seconds ago
      if (!isLoadingRef.current && now - lastLoadTimeRef.current > 2000) {
        lastLoadTimeRef.current = now;
        loadPortfolioData();
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, activePortfolio?.id]); // Only depend on portfolio ID

  // Auto-refresh watchlist and featured stocks every 5 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(() => {
      loadWatchlistStocks();
      // Refresh prices without showing loading spinner
      loadFeaturedStocks();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Refresh portfolio data
  const refreshPortfolioData = async () => {
    try {
      setRefreshing(true);
      
      // Refresh both portfolio holdings and available stocks
      await Promise.all([
        loadPortfolioData(),
        loadAvailableStocks()
      ]);
      
      toast.success('Portfolio data refreshed with real-time prices!');
    } catch (error) {
      toast.error('Failed to refresh portfolio data');
    } finally {
      setRefreshing(false);
    }
  };

  // Search stocks using Alpha Vantage
  const searchStocks = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    try {
      setStocksLoading(true);
      
      const data = await StockService.searchStocks(query, 10); // Limit to 10 results
      
      if (data.stocks && data.stocks.length > 0) {
        // Convert Alpha Vantage format to our format with proper validation
        const formattedStocks = data.stocks.map(stock => ({
          symbol: stock.symbol || 'N/A',
          name: stock.name || stock.symbol || 'Unknown',
          price: parseFloat(stock.price) || 0,
          change: parseFloat(stock.change) || 0,
          changePercent: parseFloat(stock.changePercent) || 0
        }));
        setSearchResults(formattedStocks);
        setShowSearchDropdown(true);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    } catch (error) {
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setStocksLoading(false);
    }
  };

  // Handle search input with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchStocks(searchTerm);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown container
      if (
        !event.target.closest('.portfolio-search-container') &&
        !event.target.closest('.portfolio-featured-container') &&
        !event.target.closest('.portfolio-holdings-dropdown-container')
      ) {
        setShowSearchDropdown(false);
        setShowWatchlistDropdown(false);
        setShowPortfolioDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load active portfolio
  const loadActivePortfolio = async () => {
    try {
      // Use the main portfolio route which handles getting/creating the active portfolio
      const data = await PortfolioService.getPortfolio();
      if (data.portfolio) {
        setActivePortfolio(data.portfolio);
        return data.portfolio;
      }
    } catch (error) {
      console.error('Failed to load portfolio:', error);
      // Fallback: try to get active portfolio from portfolios route if available
      try {
        const activeData = await PortfolioService.getActivePortfolio();
        if (activeData.portfolio) {
          setActivePortfolio(activeData.portfolio);
          return activeData.portfolio;
        }
      } catch (fallbackError) {
        console.error('Failed to load active portfolio:', fallbackError);
      }
    }
    return null;
  };

  // Load portfolio data from backend with real-time prices
  const loadPortfolioData = async (portfolioOverride = null) => {
    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      lastLoadTimeRef.current = Date.now();

      // Use provided portfolio, then activePortfolio state, then fallback to default
      const portfolioToUse = portfolioOverride || activePortfolio;
      const portfolioId = portfolioToUse?.id;
      
      // Clear stocks immediately when switching portfolios to prevent showing stale data
      actions.updatePortfolio({
        stocks: []
      });
      
      const data = portfolioId 
        ? await PortfolioService.getHoldings(portfolioId)
        : await PortfolioService.getHoldings();

      // Update local state with backend data
      if (data.portfolio) {
        actions.updatePortfolio({
          balance: parseFloat(data.portfolio.balance),
          totalValue: parseFloat(data.portfolio.totalValue),
          totalInvested: parseFloat(data.portfolio.totalInvested),
          totalCurrentValue: parseFloat(data.portfolio.totalCurrentValue),
          totalAllTimeGainLoss: parseFloat(data.portfolio.totalAllTimeGainLoss),
          totalAllTimeGainLossPercent: parseFloat(data.portfolio.totalAllTimeGainLossPercent),
          totalDailyGainLoss: parseFloat(data.portfolio.totalDailyGainLoss),
          totalDailyGainLossPercent: parseFloat(data.portfolio.totalDailyGainLossPercent),
          quizEarnings: parseFloat(data.portfolio.quizEarnings) || 0
        });
      }

      // Convert holdings to stock format for compatibility
      if (data.holdings && data.holdings.length > 0) {
        
        const stocks = data.holdings.map(holding => ({
          symbol: holding.symbol,
          name: holding.name,
          assetType: holding.assetType || detectAssetType(holding.symbol),
          shares: holding.shares,
          buyPrice: holding.averageCost,
          currentPrice: holding.currentPrice,
          currentValue: holding.currentValue,
          allTimeGainLoss: holding.allTimeGainLoss,
          allTimeGainLossPercent: holding.allTimeGainLossPercent,
          dailyGainLoss: holding.dailyGainLoss,
          dailyGainLossPercent: holding.dailyGainLossPercent,
          dailyChange: holding.dailyChange,
          dailyChangePercent: holding.dailyChangePercent
        }));


        actions.updatePortfolio({
          stocks
        });

        
        // Force a re-render check
        setTimeout(() => {
        }, 100);

        // Fetch real-time prices for portfolio stocks using scraped data
        await fetchRealTimePricesForPortfolio(data.holdings || [], stocks);
      } else {
        // Clear stocks if portfolio has no holdings
        actions.updatePortfolio({
          stocks: []
        });
      }

    } catch (error) {
      
      // Check if it's a network error (backend not running)
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        toast.error('Cannot connect to server. Please ensure the backend is running on port 5000.');
      } else if (error.message.includes('<!DOCTYPE')) {
        toast.error('Server returned HTML instead of JSON. Check if backend is running correctly.');
      } else if (error.message && (error.message.includes('429') || error.message.includes('Too many'))) {
        // Don't show error for rate limiting - just log it and wait for next interval
        console.warn('Rate limit reached, will retry on next interval');
      } else {
        toast.error(`Failed to load portfolio data: ${error.message}`);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Fetch real-time prices for portfolio stocks using scraped data
  const fetchRealTimePricesForPortfolio = async (holdings, currentStocks) => {
    try {
      
      // Get unique symbols from holdings
      const symbols = [...new Set(holdings.map(holding => holding.symbol))];
      
      // Fetch real-time data for each symbol using the scraped data endpoint
      const pricePromises = symbols.map(async (symbol) => {
        try {
          const data = await StockService.getStockPrice(symbol);
          return { symbol, data };
        } catch (error) {
          return { symbol, data: null };
        }
      });

      const priceResults = await Promise.all(pricePromises);
      
      // Update portfolio stocks with real-time prices using the passed stocks instead of state
      const updatedStocks = currentStocks.map(stock => {
        const priceData = priceResults.find(result => result.symbol === stock.symbol);
        if (priceData && priceData.data) {
          const newPrice = parseFloat(priceData.data.price);
          return {
            ...stock,
            currentPrice: newPrice,
            name: priceData.data.name || stock.name
          };
        }
        return stock;
      });


      // Update the portfolio state with real-time prices
      actions.updatePortfolio({
        stocks: updatedStocks
      });

      
    } catch (error) {
    }
  };

  // Check if portfolio is unlocked (requires 2 completed courses)
  const isPortfolioUnlocked = state.progress.completedLessons.length >= 2;

  // Filter stocks - show watchlist at top, then featured stocks when no search, search results when searching
  const filteredStocks = searchTerm.trim() && showSearchDropdown
    ? searchResults
    : [...watchlistStocks, ...featuredStocks]; // Show watchlist first, then top 10 most traded

  const handleBuyStock = async () => {
    if (!selectedStock) return;
    
    // Detect asset type first to determine parsing method
    const assetType = detectAssetType(selectedStock.symbol);
    
    // Parse quantity - use parseFloat for crypto (allows decimals), parseInt for stocks/ETFs
    const parsedQuantity = assetType === 'crypto' 
      ? parseFloat(quantity) || 0 
      : parseInt(quantity) || 0;
    
    if (parsedQuantity <= 0) {
      const minQuantity = assetType === 'crypto' ? '0.00000001' : '1';
      toast.error(`Please enter a valid quantity (minimum ${minQuantity})`);
      return;
    }

    // Validate stock data (price is just for display, backend will fetch latest)
    const stockPrice = parseFloat(selectedStock.price) || 0;
    if (stockPrice <= 0) {
      toast.error('Invalid stock price. Please try again.');
      return;
    }

    // Estimate total cost for validation (backend will use actual price)
    const estimatedCost = stockPrice * parsedQuantity;
    if (estimatedCost > state.portfolio.balance) {
      toast.error('Insufficient balance!');
      return;
    }

    try {
      
      // SECURITY FIX: Backend will fetch the latest price, we just pass symbol and quantity
      // The price parameter is kept for backward compatibility but backend ignores it
      const portfolioId = activePortfolio?.id || null;
      const result = await PortfolioService.buyStock(selectedStock.symbol, parsedQuantity, stockPrice, portfolioId, assetType);

      // Update local state with the response from backend
      actions.updatePortfolio({
        balance: parseFloat(result.portfolio.balance),
        totalValue: parseFloat(result.portfolio.totalValue)
      });

      // Calculate actual cost from result or use estimated cost
      const actualPrice = parseFloat(result.transaction.price) || stockPrice;
      const actualTotal = actualPrice * parsedQuantity;

      // Add transaction to local state for immediate UI update
      const newTransaction = {
        id: result.transaction.id,
        type: 'buy',
        symbol: selectedStock.symbol,
        shares: parsedQuantity,
        price: actualPrice,
        total: actualTotal,
        timestamp: new Date(result.transaction.createdAt)
      };

      // Update local portfolio state
      const updatedStocks = [...state.portfolio.stocks];
      const existingStockIndex = updatedStocks.findIndex(stock => stock.symbol === selectedStock.symbol);
      
      if (existingStockIndex >= 0) {
        // Update existing stock
        updatedStocks[existingStockIndex].shares += parsedQuantity;
      } else {
        // Add new stock
        updatedStocks.push({
          symbol: selectedStock.symbol,
          name: selectedStock.name,
          shares: parsedQuantity,
          buyPrice: selectedStock.price,
          currentPrice: selectedStock.price
        });
      }

      actions.updatePortfolio({
        stocks: updatedStocks,
        transactions: [...state.portfolio.transactions, newTransaction]
      });

      // Gamification effects
      soundEffects.playBuy();
      const buyButton = document.querySelector('.portfolio-modal-button-primary');
      if (buyButton) {
        createSuccessConfetti(buyButton);
      }
      
      // Use result's assetType if available, otherwise use the one we detected earlier
      const finalAssetType = result.assetType || assetType;
      const assetLabel = finalAssetType === 'crypto' ? 'coins' : 'shares';
      toast.success(`✅ Bought ${parsedQuantity} ${assetLabel} of ${selectedStock.symbol} for $${actualTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      setShowBuyModal(false);
      setSelectedStock(null);
      setQuantity('');

    } catch (error) {
      toast.error(`Failed to buy stock: ${error.message}`);
    }
  };

  const handleSellStock = async () => {
    if (!stockToSell) return;
    
    // Detect asset type first to determine parsing method
    const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
    
    // Parse sell quantity - use parseFloat for crypto (allows decimals), parseInt for stocks/ETFs
    const parsedSellQuantity = assetType === 'crypto' 
      ? parseFloat(sellQuantity) || 0 
      : parseInt(sellQuantity) || 0;
    
    if (parsedSellQuantity <= 0) {
      const minQuantity = assetType === 'crypto' ? '0.00000001' : '1';
      toast.error(`Please enter a valid quantity (minimum ${minQuantity})`);
      return;
    }

    // Use scraped currentPrice first, then fallback to buyPrice (for display only)
    const displayPrice = stockToSell.currentPrice || stockToSell.buyPrice;
    const estimatedValue = displayPrice * parsedSellQuantity;

    try {
      // SECURITY FIX: Backend will fetch the latest price, we just pass symbol and quantity
      // The price parameter is kept for backward compatibility but backend ignores it
      const portfolioId = activePortfolio?.id || null;
      const result = await PortfolioService.sellStock(stockToSell.symbol, parsedSellQuantity, displayPrice, portfolioId);

      // Update local state with the response from backend
      actions.updatePortfolio({
        balance: parseFloat(result.portfolio.balance),
        totalValue: parseFloat(result.portfolio.totalValue)
      });

      // Add transaction to local state for immediate UI update
      // Use the actual price from backend response
      const actualPrice = parseFloat(result.transaction.price) || displayPrice;
      const actualTotal = actualPrice * parsedSellQuantity;
      const assetType = result.assetType || stockToSell.assetType || detectAssetType(stockToSell.symbol);
      
      const newTransaction = {
        id: result.transaction.id,
        type: 'sell',
        symbol: stockToSell.symbol,
        shares: parsedSellQuantity,
        price: actualPrice,
        total: actualTotal,
        timestamp: new Date(result.transaction.createdAt),
        assetType: assetType
      };

      // Update local portfolio state
      const updatedStocks = [...state.portfolio.stocks];
      const stockIndex = updatedStocks.findIndex(stock => stock.symbol === stockToSell.symbol);
      
      if (stockIndex >= 0) {
        updatedStocks[stockIndex].shares -= parsedSellQuantity;
        if (updatedStocks[stockIndex].shares <= 0) {
          updatedStocks.splice(stockIndex, 1);
        }
      }

      actions.updatePortfolio({
        stocks: updatedStocks,
        transactions: [...state.portfolio.transactions, newTransaction]
      });

      // Gamification effects
      soundEffects.playSell();
      const sellButton = document.querySelector('.portfolio-modal-button-secondary');
      if (sellButton) {
        createSuccessConfetti(sellButton);
      }
      
      // assetType was already declared above, reuse it
      const assetLabel = assetType === 'crypto' ? 'coins' : 'shares';
      toast.success(`✅ Sold ${parsedSellQuantity} ${assetLabel} of ${stockToSell.symbol} for $${actualTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      setShowSellModal(false);
      setStockToSell(null);
      setSellQuantity('');

    } catch (error) {
      toast.error(`Failed to sell stock: ${error.message}`);
    }
  };

  const calculatePortfolioValue = () => {
    const stockValue = state.portfolio.stocks.reduce((total, stock) => {
      // Use scraped currentPrice first, then fallback to buyPrice or averagePrice
      const currentPrice = stock.currentPrice || stock.buyPrice || stock.averagePrice || 0;
      const shares = stock.shares || stock.quantity || 0;
      return total + (currentPrice * shares);
    }, 0);
    return (state.portfolio.balance || 0) + stockValue;
  };

  // Get league starting balance based on league type
  const getLeagueBaseline = () => {
    const league = activePortfolio?.league || '10k';
    const leagueBalances = {
      '10k': 10000,
      '100k': 100000,
      '1M': 1000000
    };
    return leagueBalances[league] || 10000; // Default to 10k if league not found
  };

  const calculateTotalGainLoss = () => {
    const currentTotalValue = calculatePortfolioValue();
    const quizEarnings = parseFloat(state.portfolio.quizEarnings) || 0;
    const leagueBaseline = getLeagueBaseline();
    const baseline = leagueBaseline + quizEarnings; // League starting balance + quiz earnings
    return currentTotalValue - baseline;
  };

  const calculateTotalGainLossPercent = () => {
    const quizEarnings = parseFloat(state.portfolio.quizEarnings) || 0;
    const leagueBaseline = getLeagueBaseline();
    const baseline = leagueBaseline + quizEarnings; // League starting balance + quiz earnings
    if (baseline === 0) return 0;
    const gainLoss = calculateTotalGainLoss();
    return (gainLoss / baseline) * 100;
  };

  if (!isPortfolioUnlocked) {
    return (
      <div className="portfolio-locked-container">
        {/* Top Bar */}
        <div className="portfolio-top-bar">
          <div className="portfolio-top-bar-content">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigate('/dashboard')} className="portfolio-back-button">
                ← Back
              </button>
              <h1 className="portfolio-title">Portfolio</h1>
            </div>
          </div>
        </div>

        <div className="portfolio-locked-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="portfolio-locked-card"
          >
            <div className="portfolio-locked-icon">
              <FaLock />
            </div>
            <h2 className="portfolio-locked-title">
              Portfolio Locked
            </h2>
            <p className="portfolio-locked-description">
              Complete 2 learning modules to unlock the portfolio simulator and start practicing with virtual trading.
            </p>
            <div className="portfolio-locked-progress">
              <p className="portfolio-locked-progress-text">
                <strong>Progress:</strong> {state.progress.completedLessons.length}/2 modules completed
              </p>
            </div>
            <button
              onClick={() => navigate('/learn')}
              className="portfolio-locked-button"
            >
              Start Learning
            </button>
          </motion.div>
        </div>

      {/* Bottom Navigation */}
      <BottomNav />
      </div>
    );
  }

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="portfolio-container">
        <div className="portfolio-loading-container">
          <div className="portfolio-loading-spinner"></div>
          <span className="portfolio-loading-text">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="portfolio-container">
      {/* Top Bar */}
      <div className="portfolio-top-bar">
        <div className="portfolio-top-bar-content">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => isChallengePortfolio ? navigate(-1) : navigate('/dashboard')} 
              className="portfolio-back-button"
            >
              ←
            </button>
            <h1 className="portfolio-title">Portfolio</h1>
            {isChallengePortfolio && (
              <span className="challenge-portfolio-badge">
                <FaTrophy /> Challenge Portfolio
              </span>
            )}
          </div>
           <div className="portfolio-selector-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
             {simulationsNav && onSwitchTab && (
               <button
                 onClick={onSwitchTab}
                 className="sim-portfolio-tab-switch"
                 title="Switch to Game of Life"
               >
                 🎯 Game of Life
               </button>
             )}
             <PortfolioSelector
               activePortfolio={activePortfolio}
               onPortfolioChange={(portfolio) => {
                 setActivePortfolio(portfolio);
                 setIsChallengePortfolio(!!portfolio.challengeId);
                 loadPortfolioData(portfolio);
               }}
             />
             <ProfileMenu />
           </div>
        </div>
      </div>

      <div className="portfolio-main-content">
        <div className="portfolio-content-grid">
          {/* Portfolio Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="portfolio-summary-card"
          >
            <div className="portfolio-summary-header">
              <h2 className="portfolio-summary-title">Portfolio Summary</h2>
              <div className="portfolio-action-buttons">
                <button
                  onClick={refreshPortfolioData}
                  disabled={refreshing}
                  className="portfolio-refresh-button"
                >
                  <div className="portfolio-refresh-icon">
                    {refreshing ? (
                      <div className="portfolio-refresh-spinner"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </div>
                  <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                </button>
                <button
                  onClick={() => navigate('/watchlist')}
                  className="portfolio-watchlist-button"
                >
                  <FaStar className="text-sm" />
                  <span>Watchlist</span>
                </button>
                <button
                  onClick={() => setShowTimeGraph(true)}
                  className="portfolio-graph-button"
                >
                  <FaChartArea className="text-sm" />
                  <span>View Graph</span>
                </button>
              </div>
            </div>
            <div className="portfolio-stats-grid">
              <div className="portfolio-stat-item">
                <p className="portfolio-stat-label">Cash Balance</p>
                <p className="portfolio-stat-value">${(state.portfolio.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div className="portfolio-stat-item">
                <p className="portfolio-stat-label">Total Value</p>
                <p className="portfolio-stat-value">${calculatePortfolioValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="portfolio-gainloss-section">
              <div className="portfolio-gainloss-content">
                <span className="portfolio-gainloss-label">Gain/Loss</span>
                <div className="portfolio-gainloss-values">
                  <span className={`portfolio-gainloss-amount ${
                    calculateTotalGainLoss() >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                  }`}>
                    {calculateTotalGainLoss() >= 0 ? '+' : ''}${calculateTotalGainLoss().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className={`portfolio-gainloss-percent ${
                    calculateTotalGainLossPercent() >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                  }`}>
                    ({calculateTotalGainLossPercent() >= 0 ? '+' : ''}{calculateTotalGainLossPercent().toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* My Holdings - Grouped by Asset Type */}
          {state.portfolio.stocks && state.portfolio.stocks.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="portfolio-mystocks-card"
            >
              <div className="portfolio-mystocks-header">
                <h3 className="portfolio-mystocks-title">My Holdings</h3>
                {/* Light Gray Toggle */}
                <div className="portfolio-toggle-container">
                  <div className="portfolio-toggle-wrapper">
                    {/* Sliding Background Indicator */}
                    <div 
                      className={`portfolio-toggle-slider ${
                        gainLossView === 'alltime' ? 'all-time' : 'daily'
                      }`}
                    />
                    
                    {/* Toggle Buttons */}
                    <button
                      onClick={() => setGainLossView('alltime')}
                      className={`portfolio-toggle-button ${
                        gainLossView === 'alltime' ? 'active' : 'inactive'
                      }`}
                    >
                      All-Time
                    </button>
                    <button
                      onClick={() => setGainLossView('daily')}
                      className={`portfolio-toggle-button ${
                        gainLossView === 'daily' ? 'active' : 'inactive'
                      }`}
                    >
                      Daily
                    </button>
                  </div>
                </div>
              </div>
              <div className="portfolio-holdings-list">
                {(() => {
                  // Group holdings by asset type
                  const stocks = (state.portfolio.stocks || []).filter(s => s != null && (s.assetType || detectAssetType(s.symbol)) === 'stock');
                  const etfs = (state.portfolio.stocks || []).filter(s => s != null && (s.assetType || detectAssetType(s.symbol)) === 'etf');
                  const cryptos = (state.portfolio.stocks || []).filter(s => s != null && (s.assetType || detectAssetType(s.symbol)) === 'crypto');
                  
                  const renderHolding = (stock, index) => {
                    // Use scraped currentPrice first, then fallback to buyPrice or averagePrice
                    // Ensure all values are valid numbers, defaulting to 0 if undefined/null/NaN
                    const currentPrice = safeParseFloat(
                      stock.currentPrice,
                      safeParseFloat(stock.buyPrice, safeParseFloat(stock.averagePrice))
                    );
                    const buyPrice = safeParseFloat(stock.buyPrice, safeParseFloat(stock.averagePrice));
                    const shares = safeParseFloat(stock.shares, safeParseFloat(stock.quantity));
                    const assetType = stock.assetType || detectAssetType(stock.symbol);
                    const assetLabel = assetType === 'crypto' ? 'coins' : 'shares';
                    
                    // Always calculate current value based on current price and shares
                    const currentValue = safeParseFloat(currentPrice * shares);
                    
                    // All-time gain/loss - ensure result is a valid number
                    const calculatedAllTimeGainLoss = (currentPrice - buyPrice) * shares;
                    const allTimeGainLoss = safeParseFloat(
                      stock.allTimeGainLoss,
                      safeParseFloat(calculatedAllTimeGainLoss)
                    );
                    
                    // All-time gain/loss percent
                    const calculatedAllTimeGainLossPercent = buyPrice > 0 ? (((currentPrice - buyPrice) / buyPrice) * 100) : 0;
                    const allTimeGainLossPercent = safeParseFloat(
                      stock.allTimeGainLossPercent,
                      safeParseFloat(calculatedAllTimeGainLossPercent)
                    );
                    
                    // Daily gain/loss - ensure result is a valid number
                    const dailyGainLoss = safeParseFloat(stock.dailyGainLoss);
                    const dailyGainLossPercent = safeParseFloat(stock.dailyGainLossPercent);
                    
                    return (
                      <div key={`${assetType}-${index}`} className="portfolio-holding-item">
                        <div className="portfolio-holding-header">
                          <div className="portfolio-holding-info">
                            <h4 className="portfolio-holding-symbol">{stock.symbol}</h4>
                            <p className="portfolio-holding-details">{shares} {assetLabel} @ ${(isNaN(buyPrice) ? 0 : buyPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} avg</p>
                          <p className={`portfolio-holding-current-price ${
                            currentPrice > buyPrice ? 'positive' : 
                            currentPrice < buyPrice ? 'negative' : 'neutral'
                          }`}>
                            Current: ${(isNaN(currentPrice) ? 0 : currentPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="portfolio-holding-value">
                          <p className="portfolio-holding-total-value">${(isNaN(currentValue) ? 0 : currentValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          <p className="portfolio-holding-total-label">Total Value</p>
                        </div>
                        <button
                          onClick={() => {
                            setStockToSell(stock);
                            setSellQuantity('');
                            setShowSellModal(true);
                          }}
                          className="portfolio-sell-button"
                        >
                          Sell
                        </button>
                      </div>
                      
                      <div className="portfolio-holding-gainloss">
                        <div className="portfolio-holding-gainloss-values">
                          {gainLossView === 'alltime' ? (
                            <>
                              <p className={`portfolio-holding-gainloss-amount ${
                                allTimeGainLoss >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                              }`}>
                                {allTimeGainLoss >= 0 ? '+' : ''}${(isNaN(allTimeGainLoss) ? 0 : allTimeGainLoss).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className={`portfolio-holding-gainloss-percent ${
                                allTimeGainLossPercent >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                              }`}>
                                {allTimeGainLossPercent >= 0 ? '+' : ''}{(isNaN(allTimeGainLossPercent) ? 0 : allTimeGainLossPercent).toFixed(1)}%
                              </p>
                            </>
                          ) : (
                            <>
                              <p className={`portfolio-holding-gainloss-amount ${
                                dailyGainLoss >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                              }`}>
                                {dailyGainLoss >= 0 ? '+' : ''}${(isNaN(dailyGainLoss) ? 0 : dailyGainLoss).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                              <p className={`portfolio-holding-gainloss-percent ${
                                dailyGainLossPercent >= 0 ? 'portfolio-gainloss-positive' : 'portfolio-gainloss-negative'
                              }`}>
                                {dailyGainLossPercent >= 0 ? '+' : ''}{(isNaN(dailyGainLossPercent) ? 0 : dailyGainLossPercent).toFixed(1)}%
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  };
                  
                  return (
                    <>
                      {stocks.length > 0 && (
                        <>
                          <div className="portfolio-holdings-section-header">
                            <h4 className="portfolio-holdings-section-title">Stocks</h4>
                          </div>
                          {stocks.map((stock, index) => renderHolding(stock, index))}
                        </>
                      )}
                      {etfs.length > 0 && (
                        <>
                          <div className="portfolio-holdings-section-header">
                            <h4 className="portfolio-holdings-section-title">ETFs</h4>
                          </div>
                          {etfs.map((stock, index) => renderHolding(stock, index))}
                        </>
                      )}
                      {cryptos.length > 0 && (
                        <>
                          <div className="portfolio-holdings-section-header">
                            <h4 className="portfolio-holdings-section-title">Crypto Wallet</h4>
                          </div>
                          {cryptos.map((stock, index) => renderHolding(stock, index))}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
          </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="portfolio-mystocks-card"
            >
              <div className="portfolio-empty-state">
                <h3 className="portfolio-empty-title">My Holdings</h3>
                <p className="portfolio-empty-description">No holdings in your portfolio yet</p>
                <p className="portfolio-empty-debug">
                  Debug: Portfolio stocks array length: {state.portfolio.stocks ? state.portfolio.stocks.length : 'undefined'}
                </p>
                <p className="portfolio-empty-debug">
                  Portfolio state: {JSON.stringify(state.portfolio, null, 2)}
                </p>
              </div>
            </motion.div>
          )}

          {/* Search Bar with Dropdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="portfolio-search-container"
          >
            <FaSearch className="portfolio-search-icon" />
            <input
              type="text"
              placeholder="Search stocks..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Close other dropdowns when searching
                setShowWatchlistDropdown(false);
                setShowPortfolioDropdown(false);
              }}
              onFocus={() => {
                if (searchTerm.trim() && searchResults.length > 0) {
                  setShowSearchDropdown(true);
                }
                // Close other dropdowns when focusing search
                setShowWatchlistDropdown(false);
                setShowPortfolioDropdown(false);
              }}
              onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
              className="portfolio-search-input"
            />
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="portfolio-dropdown">
                {searchResults.map((stock) => (
                  <div
                    key={stock.symbol}
                    className="portfolio-dropdown-item"
                    onClick={() => {
                      setSelectedStock(stock);
                      setQuantity('');
                      setShowBuyModal(true);
                      setShowSearchDropdown(false);
                      setSearchTerm('');
                    }}
                  >
                    <div>
                      <strong>{stock.symbol}</strong> - {stock.name}
                    </div>
                    <div>${stock.price.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Portfolio Holdings Dropdown - Show first if user has stocks */}
          {state.portfolio.stocks && state.portfolio.stocks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="portfolio-holdings-dropdown-container"
            >
              <button
                onClick={() => {
                  setShowPortfolioDropdown(!showPortfolioDropdown);
                  // Close other dropdowns when opening this one
                  if (!showPortfolioDropdown) {
                    setShowWatchlistDropdown(false);
                    setShowSearchDropdown(false);
                  }
                }}
                className="portfolio-holdings-toggle"
              >
                <span>My Portfolio ({state.portfolio.stocks.length})</span>
                <span>{showPortfolioDropdown ? '▼' : '▶'}</span>
              </button>
              {showPortfolioDropdown && (
                <div className="portfolio-dropdown portfolio-holdings-dropdown">
                  {state.portfolio.stocks.map((stock, index) => {
                    const currentPrice = safeParseFloat(
                      stock.currentPrice,
                      safeParseFloat(stock.buyPrice, safeParseFloat(stock.averagePrice))
                    );
                    const shares = safeParseFloat(stock.shares, safeParseFloat(stock.quantity));
                    const currentValue = safeParseFloat(currentPrice * shares);
                    
                    return (
                      <div
                        key={index}
                        className="portfolio-dropdown-item portfolio-holding-dropdown-item"
                        onClick={() => {
                          setStockToSell(stock);
                          setSellQuantity('');
                          setShowSellModal(true);
                          setShowPortfolioDropdown(false);
                        }}
                      >
                        <div>
                          <strong>{stock.symbol}</strong> - {shares} shares
                        </div>
                        <div>${currentValue.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* Watchlist Stocks Dropdown */}
          {watchlistStocks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14 }}
              className="portfolio-featured-container"
            >
              <button
              onClick={() => {
                setShowWatchlistDropdown(!showWatchlistDropdown);
                // Close other dropdowns when opening this one
                if (!showWatchlistDropdown) {
                  setShowPortfolioDropdown(false);
                  setShowSearchDropdown(false);
                }
              }}
                className="portfolio-featured-toggle"
              >
                <span>
                  <FaStar style={{ color: '#fbbf24', marginRight: '8px' }} />
                  Watchlist ({watchlistStocks.length})
                </span>
                <span>{showWatchlistDropdown ? '▼' : '▶'}</span>
              </button>
              {showWatchlistDropdown && (
                <div className="portfolio-dropdown portfolio-featured-dropdown">
                  {watchlistStocks.map((stock) => (
                    <div
                      key={stock.symbol}
                      className="portfolio-dropdown-item"
                      onClick={() => {
                        setSelectedStock(stock);
                        setQuantity('');
                        setShowBuyModal(true);
                        setShowWatchlistDropdown(false);
                      }}
                    >
                      <div>
                        <strong>{stock.symbol}</strong> - {stock.name}
                      </div>
                      <div>${stock.price.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Stock List - Show watchlist stocks at top, then featured stocks */}
          {!searchTerm.trim() && (
            <div className="portfolio-stock-list">
              {stocksLoading ? (
                <div className="portfolio-loading-container">
                  <div className="portfolio-loading-spinner"></div>
                  <span className="portfolio-loading-text">Loading stocks...</span>
                </div>
              ) : filteredStocks.length === 0 ? (
                <div className="portfolio-empty-state">
                  <p>No stocks available</p>
                  <p className="portfolio-empty-description">Start trading or add stocks to your watchlist!</p>
                </div>
              ) : (
                filteredStocks.map((stock, index) => (
                  <motion.div
                    key={stock.symbol}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`portfolio-stock-item ${stock.isWatchlist ? 'watchlist-item' : ''}`}
                  >
                    <div className="portfolio-stock-content">
                      <div className="portfolio-stock-info">
                        <div className="flex items-center space-x-3">
                          {stock.isWatchlist && <FaStar className="text-yellow-400" />}
                          <div>
                            <h3 className="portfolio-stock-symbol">{stock.symbol}</h3>
                            <p className="portfolio-stock-name">{stock.name}</p>
                          </div>
                        </div>
                      </div>
                      <div className="portfolio-stock-price">
                        <p>${(parseFloat(stock.price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        {stock.changePercent !== 0 && (
                          <p className={`portfolio-stock-change ${stock.changePercent >= 0 ? 'positive' : 'negative'}`}>
                            {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedStock(stock);
                          setQuantity('');
                          setShowBuyModal(true);
                          setSearchTerm('');
                          setShowSearchDropdown(false);
                        }}
                        className="portfolio-buy-button"
                      >
                        Buy
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
          
          {/* Show search results if searching */}
          {searchTerm.trim() && searchResults.length > 0 && (
            <div className="portfolio-stock-list">
              {searchResults.map((stock, index) => (
                <motion.div
                  key={stock.symbol}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="portfolio-stock-item"
                >
                  <div className="portfolio-stock-content">
                    <div className="portfolio-stock-info">
                      <div className="flex items-center space-x-3">
                        <div>
                          <h3 className="portfolio-stock-symbol">{stock.symbol}</h3>
                          <p className="portfolio-stock-name">{stock.name}</p>
                        </div>
                      </div>
                    </div>
                    <div className="portfolio-stock-price">
                      <p>${(parseFloat(stock.price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedStock(stock);
                        setQuantity('');
                        setShowBuyModal(true);
                        setSearchTerm('');
                        setShowSearchDropdown(false);
                      }}
                      className="portfolio-buy-button"
                    >
                      Buy
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Buy Modal */}
      {showBuyModal && selectedStock && (
        <div className="portfolio-modal-overlay">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="portfolio-modal"
          >
            <h3 className="portfolio-modal-title">
              Buy {selectedStock.symbol}
            </h3>
            {(() => {
              const assetType = detectAssetType(selectedStock.symbol);
              const assetLabel = assetType === 'crypto' ? 'Crypto' : assetType === 'etf' ? 'ETF' : 'Stock';
              return <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>{assetLabel}</p>;
            })()}
            <div className="portfolio-modal-content">
              <div className="portfolio-modal-field">
                <p className="portfolio-modal-label">Current Price</p>
                <p className="portfolio-modal-price">${selectedStock.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div className="portfolio-modal-field">
                <label className="portfolio-modal-label">Quantity</label>
                {(() => {
                  const assetType = detectAssetType(selectedStock.symbol);
                  const isCrypto = assetType === 'crypto';
                  return (
                    <input
                      type="number"
                      min={isCrypto ? "0.00000001" : "1"}
                      step={isCrypto ? "0.00000001" : "1"}
                      value={quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid numbers (decimals for crypto)
                        if (value === '' || (!isNaN(value) && (isCrypto ? parseFloat(value) >= 0 : parseInt(value) >= 0))) {
                          setQuantity(value);
                        }
                      }}
                      className="portfolio-modal-input"
                      placeholder={isCrypto ? "Enter quantity (e.g., 0.5)" : "Enter quantity"}
                    />
                  );
                })()}
              </div>
              <div className="portfolio-modal-summary">
                <div className="portfolio-modal-summary-row">
                  <span>Total Cost:</span>
                  <span className="portfolio-modal-summary-value">
                    {(() => {
                      const assetType = detectAssetType(selectedStock.symbol);
                      const parsedQty = assetType === 'crypto' ? parseFloat(quantity) || 0 : parseInt(quantity) || 0;
                      return `$${((selectedStock.price * parsedQty) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()}
                  </span>
                </div>
                <div className="portfolio-modal-summary-row">
                  <span>Available Balance:</span>
                  <span className="portfolio-modal-summary-value">${(state.portfolio.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
            <div className="portfolio-modal-actions">
              <button
                onClick={() => setShowBuyModal(false)}
                className="portfolio-modal-button portfolio-modal-button-cancel"
              >
                Cancel
              </button>
                <button
                onClick={handleBuyStock}
                disabled={(() => {
                  const assetType = detectAssetType(selectedStock.symbol);
                  const parsedQty = assetType === 'crypto' ? parseFloat(quantity) || 0 : parseInt(quantity) || 0;
                  return !quantity || parsedQty <= 0 || selectedStock.price * parsedQty > state.portfolio.balance;
                })()}
                className="portfolio-modal-button portfolio-modal-button-primary"
              >
                {(() => {
                  const assetType = detectAssetType(selectedStock.symbol);
                  return assetType === 'crypto' ? 'Buy Crypto' : assetType === 'etf' ? 'Buy ETF' : 'Buy Stock';
                })()}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Sell Modal */}
      {showSellModal && stockToSell && (
        <div className="portfolio-modal-overlay">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="portfolio-modal"
          >
            <h3 className="portfolio-modal-title">
              Sell {stockToSell.symbol}
            </h3>
            {(() => {
              const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
              const assetLabel = assetType === 'crypto' ? 'Crypto' : assetType === 'etf' ? 'ETF' : 'Stock';
              return <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>{assetLabel}</p>;
            })()}
            <div className="portfolio-modal-content">
              <div className="portfolio-modal-field">
                <p className="portfolio-modal-label">Current Price</p>
                <p className="portfolio-modal-price">
                  ${(stockToSell.currentPrice || stockToSell.buyPrice || stockToSell.averagePrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="portfolio-modal-field">
                <p className="portfolio-modal-label">{(() => {
                  const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                  return assetType === 'crypto' ? 'Coins Owned' : 'Shares Owned';
                })()}</p>
                <p className="portfolio-modal-price">
                  {(() => {
                    const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                    const owned = stockToSell.shares || stockToSell.quantity || 0;
                    const label = assetType === 'crypto' ? 'coins' : 'shares';
                    return `${owned} ${label}`;
                  })()}
                </p>
              </div>
              <div className="portfolio-modal-field">
                <label className="portfolio-modal-label">Quantity to Sell</label>
                {(() => {
                  const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                  const isCrypto = assetType === 'crypto';
                  const maxQty = stockToSell.shares || stockToSell.quantity || 0;
                  return (
                    <input
                      type="number"
                      min={isCrypto ? "0.00000001" : "1"}
                      step={isCrypto ? "0.00000001" : "1"}
                      max={maxQty}
                      value={sellQuantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid numbers (decimals for crypto)
                        if (value === '' || (!isNaN(value) && (isCrypto ? parseFloat(value) >= 0 : parseInt(value) >= 0))) {
                          setSellQuantity(value);
                        }
                      }}
                      className="portfolio-modal-input"
                      placeholder={isCrypto ? "Enter quantity (e.g., 0.5)" : "Enter quantity"}
                    />
                  );
                })()}
              </div>
              <div className="portfolio-modal-summary">
                <div className="portfolio-modal-summary-row">
                  <span>Total Value:</span>
                  <span className="portfolio-modal-summary-value">
                    {(() => {
                      const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                      const parsedQty = assetType === 'crypto' ? parseFloat(sellQuantity) || 0 : parseInt(sellQuantity) || 0;
                      const price = stockToSell.currentPrice || stockToSell.buyPrice || stockToSell.averagePrice || 0;
                      return `$${((price * parsedQty) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()}
                  </span>
                </div>
                <div className="portfolio-modal-summary-row">
                  <span>Current Balance:</span>
                  <span className="portfolio-modal-summary-value">${(state.portfolio.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
            <div className="portfolio-modal-actions">
              <button
                onClick={() => setShowSellModal(false)}
                className="portfolio-modal-button portfolio-modal-button-cancel"
              >
                Cancel
              </button>
                <button
                onClick={handleSellStock}
                disabled={(() => {
                  const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                  const parsedQty = assetType === 'crypto' ? parseFloat(sellQuantity) || 0 : parseInt(sellQuantity) || 0;
                  const maxQty = stockToSell.shares || stockToSell.quantity || 0;
                  return !sellQuantity || parsedQty <= 0 || parsedQty > maxQty;
                })()}
                className="portfolio-modal-button portfolio-modal-button-secondary"
              >
                {(() => {
                  const assetType = stockToSell.assetType || detectAssetType(stockToSell.symbol);
                  return assetType === 'crypto' ? 'Sell Crypto' : assetType === 'etf' ? 'Sell ETF' : 'Sell Stock';
                })()}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Time Graph Modal */}
      {showTimeGraph && (
        <PortfolioTimeGraph 
          onClose={() => setShowTimeGraph(false)} 
          portfolioId={activePortfolio?.id || null}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Portfolio;
